import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSpinner, faLink } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import { copyLink } from '../../utils/copyLink';
import EmployeeMultiSelect from '../EmployeeMultiSelect';
import { EMAIL } from './meetingHelpers';
import '../../styles/meetings.css';

/**
 * Ask more people into a meeting that is on. Portal users are picked from the
 * directory, anyone else by email; they get an email (and portal users a bell)
 * and walk in without waiting in the lobby. The link is here too, for pasting
 * into a chat.
 */

const readUser = () => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
};

const MeetingInviteModal = ({ meetingId, link, onClose, onInvited }) => {
    const me = useMemo(readUser, []);
    const [employees, setEmployees] = useState([]);
    const [userIds, setUserIds] = useState([]);
    const [guests, setGuests] = useState([]);
    const [guestInput, setGuestInput] = useState('');
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        let alive = true;
        api.get('/employees/directory')
            .then((res) => { if (alive) setEmployees(res.data || []); })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    const people = useMemo(
        () => employees.filter((e) => String(e._id) !== String(me.id || me._id)),
        [employees, me]
    );

    const addGuests = (raw) => {
        const parts = raw.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
        if (!parts.length) return guests;
        const bad = parts.filter((p) => !EMAIL.test(p));
        if (bad.length) {
            Swal.fire('Check the email', `"${bad[0]}" is not a valid email address.`, 'warning');
            return guests;
        }
        const known = new Set(guests.map((g) => g.email));
        const fresh = parts.filter((p, i) => !known.has(p) && parts.indexOf(p) === i).map((email) => ({ email, name: '' }));
        const next = [...guests, ...fresh];
        setGuests(next);
        setGuestInput('');
        return next;
    };

    const copy = async () => {
        if (await copyLink(link)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const send = async () => {
        // Whatever is typed but not yet entered counts.
        const all = guestInput.trim() ? addGuests(guestInput) : guests;
        if (guestInput.trim() && all === guests) return; // the typed address was not valid
        if (!userIds.length && !all.length) return;
        setSending(true);
        try {
            const res = await api.post(`/meetings/${meetingId}/invite`, { userIds, guests: all });
            const { invited, skipped } = res.data;
            Swal.fire({
                title: invited ? 'Invitations sent' : 'Nothing to send',
                text: invited
                    ? `${invited} ${invited === 1 ? 'person was' : 'people were'} invited and can join straight away.${skipped ? ` ${skipped} already ${skipped === 1 ? 'had' : 'had'} access.` : ''}`
                    : 'Everyone you chose is already invited.',
                icon: invited ? 'success' : 'info'
            });
            if (invited && onInvited) onInvited();
            onClose();
        } catch (err) {
            Swal.fire('Could not invite', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="mt-overlay mt-over-room" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="mt-modal mt-invite-modal" role="dialog" aria-label="Invite people">
                <div className="mt-modal-head">
                    <h3>Invite people</h3>
                    <button type="button" className="mt-icon-btn" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faXmark} /></button>
                </div>

                <div className="mt-modal-body">
                    {link && (
                        <div className="mt-field">
                            <span>Share the link</span>
                            <div className="mt-link-row">
                                <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Meeting link" />
                                <button type="button" className="mt-btn ghost" onClick={copy}>
                                    <FontAwesomeIcon icon={faLink} /> {copied ? 'Copied' : 'Copy link'}
                                </button>
                            </div>
                            <small className="mt-subtle">Anyone who is not invited waits until a host lets them in.</small>
                        </div>
                    )}

                    <div className="mt-field">
                        <span>Portal users</span>
                        <EmployeeMultiSelect
                            employees={people}
                            selected={userIds}
                            onChange={setUserIds}
                            recentKey="meeting-guest"
                            placeholder="Search and add people..."
                            emptyText="No employees to invite."
                        />
                    </div>

                    <div className="mt-field">
                        <span>Other people, by email</span>
                        <div className="mt-guest-entry">
                            <input
                                type="text"
                                value={guestInput}
                                placeholder="name@company.com, then Enter"
                                onChange={(e) => setGuestInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addGuests(guestInput); }
                                }}
                            />
                        </div>
                        {guests.length > 0 && (
                            <div className="mt-chips">
                                {guests.map((g) => (
                                    <span key={g.email} className="mt-chip">
                                        {g.name || g.email}
                                        <button type="button" aria-label={`Remove ${g.email}`} onClick={() => setGuests(guests.filter((x) => x.email !== g.email))}>✕</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-modal-foot">
                    <button type="button" className="mt-btn ghost" onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className="mt-btn primary"
                        onClick={send}
                        disabled={sending || (!userIds.length && !guests.length && !guestInput.trim())}
                    >
                        {sending && <FontAwesomeIcon icon={faSpinner} spin />} Send invitations
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MeetingInviteModal;
